import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import { isEmpty } from 'lodash';
import React, { useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileNo } from '@/features/account-management/types';
import { LayoutContext } from 'contexts/LayoutContext';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { phoneNumberProp } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { Card } from '@utx/utx-access-client';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useNavigation } from '@react-navigation/native';
import { UpdateMobileNoStackParamList, UpdateMobileNoStackRoutes } from '@/features/account-management/screens/update-mobile-no/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface UpdateMobileNoProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: MobileNo;
  func?: (cardId: string, phoneNumber: phoneNumberProp, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: Card;
}

export const UpdateMobileNoNativeOneScreen: React.FC<UpdateMobileNoProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const { body, textMb, textTitle, textAtmPin, btnView, btn, modal } = sxStyles;

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<UpdateMobileNoStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        index={1}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View />}>
        <View sx={modal}>
          <View sx={{ minHeight: 280, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <View>
              <BottomSheetHeader
                title={t('card-account-management::replace-card.title--update-mobile-number-confirmation')}
                onPress={() => navigation.popToTop()}
              />
            </View>
            <View sx={body}>
              <TextBody sx={textMb} semiBold>
                {t('card-account-management::replace-card.text--update-mobile-number-inquiry')}
              </TextBody>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account-management::replace-card.text--new-mobile-number')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                +{data?.phoneCode} {data?.phoneNumber}
              </H6>
              {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
              <View sx={btnView}>
                <UniButton
                  loading={isLoading}
                  sx={btn}
                  label={t('card-account-management::general.button--place-instruction')}
                  onPress={() => {
                    setIsLoading(true);
                    func &&
                      func(
                        record?.id!,
                        { countryCode: data?.phoneCode!, mobileNumber: data?.phoneNumber! },
                        (returnData) => {
                          setIsLoading(false);
                          onReturnData && onReturnData(returnData.data);
                          navigation.navigate(UpdateMobileNoStackRoutes.Placed, {
                            record,
                            onLayout: onLayout,
                            data,
                            returnData: returnData.data,
                            refresh
                          });
                        },
                        (error) => {
                          setIsLoading(false);
                          setAlertMessage(error?.message);
                        }
                      );
                  }}
                />
              </View>
            </View>
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {},
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
